import React, {useState, useEffect, useRef} from 'react';
import './Wettkampf60.css';
import './NeuesErgebnis.css';
import ApiService from "../services/ApiService";

function NeuesErgebnis({token, wettkampfId, disziplin, mannschaft, matchingPersons}) {
    const [displayText, setDisplayText] = useState('');
    const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [rowsCount, setRowsCount] = useState(1);
    const positionRefs = useRef([]);

    const [rows, setRows] = useState([
        {
            position: 1,
            personId: null,
            scheibenNr: null,
            s1: 0,
            s2: 0,
            s3: 0,
            sumStehend: 0,
            s4: 0,
            s5: 0,
            s6: 0,
            sumKniend: 0,
            gesamt: 0
        }
    ]);

    // Fokussiere das Position-Feld der letzten Zeile, wenn eine neue Zeile hinzugefügt wurde
    useEffect(() => {
        if (rowsCount > 0 && positionRefs.current[rowsCount - 1]) {
            positionRefs.current[rowsCount - 1].focus();
        }
    }, [rowsCount]);

    const handleAddRow = () => {
        setIsSaveSuccessful(false);
        setHasError(false);
        const newRowCount = rows.length + 1;
        setRows([...rows, {
            position: newRowCount,
            personId: null,
            scheibenNr: null,
            s1: 0,
            s2: 0,
            s3: 0,
            sumStehend: 0,
            s4: 0,
            s5: 0,
            s6: 0,
            sumKniend: 0,
            gesamt: 0
        }]);
        setRowsCount(newRowCount);
    };
    const handleRemoveRow = (index) => {
        setIsSaveSuccessful(false);
        setHasError(false);
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
        setRowsCount(updatedRows.length);
        // Ref-Array aktualisieren
        positionRefs.current = positionRefs.current.filter((_, i) => i !== index);
    };

    const handlePersonChange = (e, index) => {
        setIsSaveSuccessful(false);
        setHasError(false);
        const updatedRows = [...rows];
        updatedRows[index].personId = e.target.value;
        setRows(updatedRows);
    };

    const handlePositionChange = (e, index) => {
        setIsSaveSuccessful(false);
        setHasError(false);
        const updatedRows = [...rows];
        updatedRows[index].position = e.target.value;
        setRows(updatedRows);
    };
    const handleScheibenChange = (rowIndex, field, value) => {
        setIsSaveSuccessful(false);
        setHasError(false);
        const updatedRows = [...rows];
        if (/^[\d\s\-–—−]+$/.test(value)) {
            updatedRows[rowIndex][field] = value.replace(/[^\d\s\-–—−_~]+/g, '-').trim();
            setDisplayText('');
        } else {
            updatedRows[rowIndex][field] = null;
            setDisplayText('Bitte geben Sie nur Zahlen oder - ein. Leerzeichen sind erlaubt');
        }
        setRows(updatedRows);
    };
    const handleSerieChange = (rowIndex, field, value) => {
        setIsSaveSuccessful(false);
        setHasError(false);
        setDisplayText('');
        const updatedRows = [...rows];
        if (/^\d{0,3}$/.test(value) && value <= 100) {
            updatedRows[rowIndex][field] = value;
            updatedRows[rowIndex].gesamt = calculateSum(updatedRows[rowIndex]);
            if (disziplin.requiredSeries === 6) {
                updatedRows[rowIndex].sumStehend = sumOfThree(updatedRows[rowIndex].s1, updatedRows[rowIndex].s2, updatedRows[rowIndex].s3);
                updatedRows[rowIndex].sumKniend = sumOfThree(updatedRows[rowIndex].s4, updatedRows[rowIndex].s5, updatedRows[rowIndex].s6);
            }
        } else {
            updatedRows[rowIndex][field] = 0;
            setDisplayText('Bitte geben Sie eine max. 3-stellige Zahl ein.');
        }
        setRows(updatedRows);
    };

    const calculateSum = (row) => {
        const filteredValues = [row.s1, row.s2, row.s3, row.s4, row.s5, row.s6].filter(value => value !== null && value !== '');
        if (filteredValues.length > 0) {
            return filteredValues.reduce((acc, curr) => acc + parseFloat(curr), 0);
        } else {
            return 0;
        }
    }

    const sumOfThree = (one, two, three) => {
        const filteredValues = [one, two, three].filter(value => value !== 0 && value !== null);
        if (filteredValues.length > 0) {
            return filteredValues.reduce((acc, curr) => acc + parseFloat(curr), 0);
        } else {
            return 0;
        }
    }

    const handleSaveButtonClick = async () => {
        try {
            if (rows.some(row => !row.personId)) {
                setHasError(true);
                setDisplayText('Schütze auswählen.');
                return;
            }
            const personIds = new Set();
            if (rows.some(row => {
                if (personIds.has(row.personId)) {
                    return true;
                } else {
                    personIds.add(row.personId);
                    return false;
                }
            })) {
                setHasError(true);
                setDisplayText('Jede Person darf nur 1 Ergebnis einreichen.');
                return;
            }
            if (rows.some(row => !row.scheibenNr)) {
                setHasError(true);
                setDisplayText('Scheibennummern angeben.');
                return;
            }

            const positions = new Set();
            if (rows.some(row => {
                if (positions.has(row.position)) {
                    return true; // Position kommt mehr als einmal vor
                } else {
                    positions.add(row.position);
                    return false;
                }
            })) {
                setHasError(true);
                setDisplayText('Positionen überprüfen.');
                return;
            }

            if (rows.length < disziplin.requiredStarters) {
                const confirmSave = window.confirm(
                    'Die Mannschaft ist nicht vollständig. Sind Sie sicher, dass Sie speichern wollen? ' +
                    'Ihre Mannschaft gilt dann als nicht angetreten und verliert den Wettkampf.'
                );
                if (!confirmSave) {
                    return; // Der Benutzer hat Abbrechen ausgewählt
                }
            }
            const ergebnisDto = {
                wettkampfId: Number(wettkampfId),
                mannschaftId: Number(mannschaft.id),
                ergebnisse: rows.map(row => ({
                    position: Number(row.position),
                    personId: Number(row.personId),
                    scheibenNr: row.scheibenNr,
                    s1: Number(row.s1),
                    s2: Number(row.s2),
                    s3: Number(row.s3),
                    sumStehend: Number(row.sumStehend),
                    s4: Number(row.s4),
                    s5: Number(row.s5),
                    s6: Number(row.s6),
                    sumKniend: Number(row.sumKniend),
                    gesamt: Number(row.gesamt)
                }))
            };
            const response = await ApiService.createErgebnisse(token, ergebnisDto);
            if (response) {
                setDisplayText(`Daten erfolgreich gespeichert`);
                setIsSaveSuccessful(true);
                setHasError(false);
            } else {
                setIsSaveSuccessful(false);
                setHasError(true);
                setDisplayText("allgemeiner Fehler");
            }
        } catch (error) {
            setIsSaveSuccessful(false)
            setHasError(true);
            setDisplayText(error.message);
        }
    };

    const renderRows = () => {
        return rows.map((row, index) => (
            <div key={index} className="ergebnis-row">
                <div className="ergebnis-row-header">
                    <div className="row-actions">
                        <button className="delete-row-btn" onClick={() => handleRemoveRow(index)} title="Zeile löschen">
                            ✕
                        </button>
                    </div>
                    <div className="row-info">
                        <div className="info-group">
                            <label htmlFor={`position-${index}`}>Position</label>
                            <select
                                ref={el => positionRefs.current[index] = el}
                                id={`position-${index}`}
                                value={row.position || ''}
                                onChange={(e) => handlePositionChange(e, index)}
                            >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="info-group">
                            <label htmlFor={`person-${index}`}>Schütze</label>
                            <select 
                                id={`person-${index}`}
                                value={row.personId || ''}
                                onChange={(e) => handlePersonChange(e, index)}
                            >
                                <option value="" disabled>
                                    --- wähle einen Schützen ---
                                </option>
                                {matchingPersons.map((person) => (
                                    <option key={person.id} value={person.id}>
                                        {person.nachname}, {person.vorname}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="info-group">
                            <label htmlFor={`scheibennr-${index}`}>Scheibennummer</label>
                            <input 
                                type="text" 
                                id={`scheibennr-${index}`}
                                onChange={(e) => handleScheibenChange(index, 'scheibenNr', e.target.value)} 
                                maxLength="15"
                                value={row.scheibenNr || ''}
                            />
                        </div>
                    </div>
                </div>

                <div className="serien-container">
                    {disziplin.requiredSeries === 6 ? (
                        <div className="serien-row">
                            <span className="serien-label">Stehend:</span>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s1-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's1', e.target.value)} 
                                    maxLength="3"
                                    value={row.s1 || ''}
                                    placeholder="S1"
                                />
                            </div>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s2-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's2', e.target.value)} 
                                    maxLength="3"
                                    value={row.s2 || ''}
                                    placeholder="S2"
                                />
                            </div>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s3-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's3', e.target.value)} 
                                    maxLength="3"
                                    value={row.s3 || ''}
                                    placeholder="S3"
                                />
                            </div>
                            <div className="sum-group">
                                <span className="sum-label">Summe:</span>
                                <span className="sum-value" id={`gesamt-stehend-${index}`}>
                                    {row.sumStehend !== null && row.sumStehend !== 0 ? row.sumStehend : '-'}
                                </span>
                            </div>
                            <span className="serien-label">Kniend:</span>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s4-kniend-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's4', e.target.value)} 
                                    maxLength="3"
                                    value={row.s4 || ''}
                                    placeholder="S4"
                                />
                            </div>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s5-kniend-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's5', e.target.value)} 
                                    maxLength="3"
                                    value={row.s5 || ''}
                                    placeholder="S5"
                                />
                            </div>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s6-kniend-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's6', e.target.value)} 
                                    maxLength="3"
                                    value={row.s6 || ''}
                                    placeholder="S6"
                                />
                            </div>
                            <div className="sum-group">
                                <span className="sum-label">Summe:</span>
                                <span className="sum-value" id={`gesamt-kniend-${index}`}>
                                    {row.sumKniend !== null && row.sumKniend !== 0 ? row.sumKniend : '-'}
                                </span>
                            </div>
                            <div className="gesamt-sum">
                                <span className="sum-label">Gesamt:</span>
                                <span className="sum-value" id={`gesamt-${index}`}>
                                    {row.gesamt !== null && row.gesamt !== 0 ? row.gesamt : '-'}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="serien-row">
                            <span className="serien-label">Serien:</span>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s1-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's1', e.target.value)} 
                                    maxLength="3"
                                    value={row.s1 || ''}
                                    placeholder="S1"
                                />
                            </div>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s2-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's2', e.target.value)} 
                                    maxLength="3"
                                    value={row.s2 || ''}
                                    placeholder="S2"
                                />
                            </div>
                            <div className="serie-input-group">
                                <input 
                                    type="text" 
                                    id={`s3-${index}`} 
                                    onChange={(e) => handleSerieChange(index, 's3', e.target.value)} 
                                    maxLength="3"
                                    value={row.s3 || ''}
                                    placeholder="S3"
                                />
                            </div>
                            {disziplin.requiredSeries === 4 && (
                                <div className="serie-input-group">
                                    <input 
                                        type="text" 
                                        id={`s4-${index}`} 
                                        onChange={(e) => handleSerieChange(index, 's4', e.target.value)} 
                                        maxLength="3"
                                        value={row.s4 || ''}
                                        placeholder="S4"
                                    />
                                </div>
                            )}
                            <div className="gesamt-sum">
                                <span className="sum-label">Gesamt:</span>
                                <span className="sum-value" id={`gesamt-${index}`}>
                                    {row.gesamt !== null && row.gesamt !== 0 ? row.gesamt : '-'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        ))
    }
    return (
        <div className="ergebnis-container">
            <div className="ergebnis-header">
                <h3>Ergebnis eintragen für {mannschaft.name}</h3>
            </div>
            {renderRows()}
            <div className="actions-container">
                <button className="add-row-btn" onClick={handleAddRow}>
                    + Weiteres Ergebnis hinzufügen
                </button>
                <div className="save-container">
                    <button 
                        className={`save-button ${isSaveSuccessful ? 'success' : ''} ${hasError ? 'error' : ''}`}
                        onClick={handleSaveButtonClick}
                    >
                        Speichern und freigeben
                    </button>
                    {displayText && (
                        <div className={`message ${isSaveSuccessful ? 'success' : ''} ${hasError ? 'error' : ''}`}>
                            {displayText}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default NeuesErgebnis;
